// US-GIFT-02/03 -- Child allocates a gift across their buckets.

import {
  type PendingGiftEntry,
  bucketsList,
  giftAllocate,
  giftMyPendingList,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  depositId?: number;
  onDone: () => void;
  onCancel: () => void;
};

export const GiftAllocationScreen: Component<Props> = (props) => {
  const [giftsResource] = createResource(async () => {
    const r = await giftMyPendingList(props.sessionId);
    return r.ok ? r.value : [];
  });

  const [bucketsResource] = createResource(async () => {
    const r = await bucketsList({ sessionId: props.sessionId });
    return r.ok ? r.value : [];
  });

  const currentGift = createMemo((): PendingGiftEntry | null => {
    const gifts = giftsResource();
    if (!gifts || gifts.length === 0) return null;
    if (props.depositId)
      return gifts.find((g) => g.depositId === props.depositId) ?? gifts[0] ?? null;
    return gifts[0] ?? null;
  });

  const [percentages, setPercentages] = createSignal<Record<number, number>>({});

  const totalPercent = createMemo(() =>
    Object.values(percentages()).reduce((s, p) => s + (Number(p) || 0), 0),
  );

  const bucketAmounts = createMemo(() => {
    const gift = currentGift();
    const buckets = bucketsResource();
    if (!gift || !buckets) return [];
    const total = gift.amountMinorUnits;
    const pcts = percentages();
    const items = buckets
      .filter((b) => (pcts[b.bucketId] ?? 0) > 0)
      .map((b) => ({ bucketId: b.bucketId, bucketName: b.bucketName, pct: pcts[b.bucketId] ?? 0 }))
      .sort((a, b) => b.pct - a.pct);
    let remaining = total;
    return items.map((item, i) => {
      const base = Math.floor((item.pct / 100) * total);
      const amount = i === items.length - 1 ? remaining : base;
      remaining -= base;
      return { bucketId: item.bucketId, bucketName: item.bucketName, amount };
    });
  });

  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const canConfirm = () => totalPercent() === 100 && !busy() && bucketAmounts().length > 0;

  async function confirm() {
    const gift = currentGift();
    if (!gift) return;
    setBusy(true);
    setError(null);
    const r = await giftAllocate({
      sessionId: props.sessionId,
      depositId: gift.depositId,
      allocations: bucketAmounts().map((b) => ({
        bucketId: b.bucketId,
        amountMinorUnits: b.amount,
      })),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message ?? 'Something went wrong.');
      return;
    }
    props.onDone();
  }

  function fmt(n: number): string {
    return `£${(n / 100).toFixed(2)}`;
  }

  return (
    <ActionScreen
      eyebrow="This one is yours to decide"
      title="Allocate a gift"
      onBack={props.onCancel}
      class="action-screen--gift-allocation"
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          onClick={confirm}
          disabled={!canConfirm()}
        >
          {busy() ? 'Saving…' : 'Save my split'}
        </button>
      }
    >
      <Show when={currentGift()} fallback={<p class="action-loading">Loading your gift…</p>}>
        <section class="gift-allocation-intro">
          <span class="action-screen-eyebrow">Your gift</span>
          <strong class="num">{fmt(currentGift()?.amountMinorUnits ?? 0)}</strong>
          <p>
            <Show when={currentGift()?.note}>{(giftNote) => <>“{giftNote()}” </>}</Show>
            Decide where it goes. Gifts do not have to follow your usual split.
          </p>
        </section>

        <Show when={error()}>
          <div class="form-error" role="alert">
            {error()}
          </div>
        </Show>

        <div class="gift-allocation-grid">
          <Show when={bucketsResource()} fallback={<p class="action-loading">Loading buckets…</p>}>
            <For each={bucketsResource()}>
              {(bucket) => (
                <div class="gift-bucket-row">
                  <div class="gift-bucket-name">{bucket.bucketName}</div>
                  <div class="gift-bucket-input">
                    <input
                      type="number"
                      class="input num gift-pct-input"
                      min={0}
                      max={100}
                      step={1}
                      value={percentages()[bucket.bucketId] ?? 0}
                      onInput={(e) => {
                        const v = Math.min(
                          100,
                          Math.max(0, Math.floor(Number(e.currentTarget.value) || 0)),
                        );
                        setPercentages((p) => ({ ...p, [bucket.bucketId]: v }));
                      }}
                      disabled={busy()}
                      aria-label={`Percentage for ${bucket.bucketName}`}
                    />
                    <span class="gift-pct-suffix">%</span>
                  </div>
                  <div class="gift-bucket-amount num">
                    {fmt(bucketAmounts().find((a) => a.bucketId === bucket.bucketId)?.amount ?? 0)}
                  </div>
                </div>
              )}
            </For>
          </Show>
        </div>

        <div
          class={`gift-total-row${totalPercent() === 100 ? ' gift-total-row--ok' : totalPercent() > 100 ? ' gift-total-row--over' : ''}`}
        >
          <span>Allocated</span>
          <span class="num">{totalPercent()}%</span>
          <span class="num">
            {fmt(bucketAmounts().reduce((sum, allocation) => sum + allocation.amount, 0))}
          </span>
        </div>

        <Show when={totalPercent() > 100}>
          <div class="form-error">That is over 100% — reduce one of the amounts.</div>
        </Show>
        <Show when={totalPercent() < 100}>
          <p class="gift-allocation-left">{100 - totalPercent()}% left to allocate</p>
        </Show>
      </Show>
    </ActionScreen>
  );
};
